import isHotkey from 'is-hotkey';
import type { ForwardedRef, ReactElement, ReactNode } from 'react';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { v4 } from 'uuid';
import { cx } from '../cx';
import { useKeyboardList } from '../hooks/use-keyboard-list';
import type { IconSize } from '../icon-svg';
import type { PcIconName } from '../icon-svg/pc-icon';
import { Icon } from '../icon-svg/pc-icon';
import { Tooltip } from '../tooltip';
import { Switch } from './switch';

export enum KeyboardItemType {
  Source = 'source',
  Mention = 'mention',
  Divider = 'divider',
  Page = 'page',
  SubMenu = 'submenu',
  Default = 'default',
  Switch = 'switch',
  Option = 'option',
}

export const NON_SELECTABLE_TYPES = [KeyboardItemType.Divider];

const isItemSelectable = (item: KeyboardItemBase): boolean => {
  const selectable = item.selectable ?? true;
  return !NON_SELECTABLE_TYPES.includes(item.type) && selectable && !item.disabled;
};

export interface KeyboardItemBase {
  id?: string;
  title?: string;
  iconName?: PcIconName;
  iconClassName?: string;
  type: KeyboardItemType;
  rightIconName?: PcIconName;
  isChecked?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  tooltip?: string;
  isMultipleChecked?: boolean;
  renderPageIcon?: () => ReactNode;
  selectable?: boolean;
  aiModel?: boolean;
  disabled?: boolean;
  renderRight?: () => ReactNode;
}

export type KeyboardItemInput<T = {}> = KeyboardItemBase & T;

export type KeyboardItem<T = {}> = KeyboardItemBase &
  T & {
    id: string;
    title: string;
  };

export function ensureItemId<T>(item: KeyboardItemInput<T>): KeyboardItem<T> {
  return {
    ...item,
    id: item.id || v4(),
    title: item.title || '',
  };
}

export interface KeyboardListProps<T = {}> {
  items: KeyboardItemInput<T>[];
  onClickItem?: (item: KeyboardItem<T>) => void;
  className?: string;
  renderItem?: (item: KeyboardItem<T>, isSelected: boolean) => ReactNode;
}

export interface KeyboardListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const ItemIcon = memo(({ item, size = 'small' }: { item: KeyboardItemBase; size?: IconSize }) => {
  if (item.renderPageIcon) {
    const icon = item.renderPageIcon();
    return icon as ReactElement;
  }
  if (item.iconName) {
    return (
      <Icon name={item.iconName} size={size} className={cx('flex-shrink-0', item.iconClassName)} />
    );
  }
  return null;
});

const getItemClassName = (
  baseClassName: string,
  { isSelected, disabled }: { isSelected: boolean; disabled?: boolean }
) =>
  cx(
    baseClassName,
    !disabled && isSelected && 'bg-black_006',
    disabled && 'opacity-30',
    !(disabled && isSelected) && 'animate-click'
  );

const DefaultItem = memo(
  <T extends {}>({ item, isSelected }: { item: KeyboardItem<T>; isSelected: boolean }) => (
    <div
      className={getItemClassName(
        'px-4 py-1.5 w-full flex items-center justify-between space-x-2',
        {
          isSelected,
          disabled: item.disabled,
        }
      )}
      onClick={item.onClick}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <ItemIcon item={item} />
        {item.title && <span className="truncate">{item.title}</span>}
      </div>
      {item.type === KeyboardItemType.Switch ? (
        <Switch bgClassName="pointer-events-none" open={item.isChecked ?? false} />
      ) : (
        <>
          {item.isChecked && <Icon name="IcCheck02" size="small" className="flex-shrink-0" />}
          {item.rightIconName && (
            <Icon name={item.rightIconName} size="small" className="flex-shrink-0 text-grey3" />
          )}
        </>
      )}
    </div>
  )
);

const SourceItem = memo(
  <T extends {}>({ item, isSelected }: { item: KeyboardItem<T>; isSelected: boolean }) => (
    <Tooltip
      popup={item.tooltip}
      placement="left"
      className={getItemClassName(
        'px-4 py-1.5 w-full rounded-none flex items-center justify-between space-x-2',
        {
          isSelected,
          disabled: item.disabled,
        }
      )}
      onClick={item.onClick}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <ItemIcon item={item} />
        {item.title && <span className="truncate">{item.title}</span>}
      </div>
      {item.isChecked && !item.isMultipleChecked && (
        <Icon name="IcCheck02" size="small" className="flex-shrink-0" />
      )}
      {item.isMultipleChecked && (
        <Icon
          name={item.isChecked ? 'IcCheckbox2Check' : 'IcCheckbox2'}
          size="small"
          className={cx('flex-shrink-0', item.isChecked ? 'text-active_color' : 'text-grey6')}
        />
      )}
    </Tooltip>
  )
);

const MentionItem = memo(
  <T extends {}>({ item, isSelected }: { item: KeyboardItem<T>; isSelected: boolean }) => (
    <div
      className={getItemClassName('px-4 py-1 flex items-center space-x-2', {
        isSelected,
        disabled: item.disabled,
      })}
      onClick={item.onClick}
    >
      <ItemIcon item={item} size="middle" />
      {item.title && <div className="truncate flex-1">{item.title}</div>}
      {item.isChecked && (
        <Icon name="IcCheck02" boxSize="small" size="small" className="flex-shrink-0" />
      )}
    </div>
  )
);

const OptionItem = memo(
  <T extends {}>({ item, isSelected }: { item: KeyboardItem<T>; isSelected: boolean }) => (
    <div
      className={getItemClassName(
        'px-4 py-1.5 w-full flex items-center justify-between space-x-2',
        {
          isSelected,
          disabled: item.disabled,
        }
      )}
      onClick={item.onClick}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <ItemIcon item={item} />
        {item.title && <span className="truncate">{item.title}</span>}
      </div>
      <div>{item.renderRight?.()}</div>
    </div>
  )
);

const DividerItem = memo(<T extends {}>({ item }: { item: KeyboardItem<T> }) => (
  <>
    {item.title ? (
      <div className="px-4 py-px text-t4 text-grey3 truncate">{item.title}</div>
    ) : (
      <div className="py-1">
        <div className="h-px bg-grey6" />
      </div>
    )}
  </>
));

function KeyboardListInner<T extends {}>(
  props: KeyboardListProps<T> & { ref?: ForwardedRef<KeyboardListRef> },
  ref: ForwardedRef<KeyboardListRef>
) {
  const { items, onClickItem, className, renderItem } = props;
  const itemsWithId = useMemo(() => items.map((item) => ensureItemId(item)), [items]);
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedIndex, handleKeyDown, setSelectedIndex } = useKeyboardList({
    items: itemsWithId,
    onSelect: onClickItem,
    circular: true,
    skipIndexes: itemsWithId
      .map((item, index) => (isItemSelectable(item) ? -1 : index))
      .filter((index) => index !== -1),
  });

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (isHotkey('escape', event)) {
        return false;
      }
      return handleKeyDown(event);
    },
  }));

  const renderBuiltInItem = (item: KeyboardItem<T>, isSelected: boolean) => {
    if (item.type === KeyboardItemType.Divider) {
      return <DividerItem item={item} />;
    }

    switch (item.type) {
      case KeyboardItemType.Source:
      case KeyboardItemType.Page:
        return <SourceItem item={item} isSelected={isSelected} />;
      case KeyboardItemType.Mention:
        return <MentionItem item={item} isSelected={isSelected} />;
      case KeyboardItemType.Option:
        return <OptionItem item={item} isSelected={isSelected} />;
      default:
        return <DefaultItem item={item} isSelected={isSelected} />;
    }
  };

  return (
    <div
      ref={containerRef}
      className={cx('flex flex-col outline-none text-t2', className)}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (isHotkey(['up', 'down', 'enter'], e)) {
          e.preventDefault();
          e.stopPropagation();
          handleKeyDown(e as unknown as KeyboardEvent);
        }
      }}
    >
      {itemsWithId.map((item, index) => (
        <div
          key={item.id}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isItemSelectable(item)) {
              setSelectedIndex(index);
              onClickItem?.(item);
              containerRef.current?.focus();
            }
          }}
          onMouseEnter={() => {
            if (isItemSelectable(item)) {
              setSelectedIndex(index);
            }
          }}
        >
          {renderItem?.(item, index === selectedIndex) ??
            renderBuiltInItem(item, index === selectedIndex)}
        </div>
      ))}
    </div>
  );
}

export const KeyboardList = forwardRef(KeyboardListInner) as <T extends {}>(
  props: KeyboardListProps<T> & { ref?: ForwardedRef<KeyboardListRef> }
) => ReactElement;
